import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Plus, Tag } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { getTagColor } from '../utils/helpers';

const COMMON_TAGS = ['VIP', 'Regular', 'New', 'Senior', 'Child', 'Student', 'Referral'];

const EditPatientTagsModal = ({ isOpen, onClose, patient, onSuccess }) => {
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (patient) {
      setTags(patient.tags || []);
    }
  }, [patient]);

  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/patients/${patient.id}`, { tags });
      toast.success('Patient tags updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update tags');
    } finally {
      setLoading(false);
    }
  };

  if (!patient) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-600" />
            Edit Patient Tags
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Patient: {patient.name}</p>
          </div>

          {/* Current Tags */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Current Tags</label>
            <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border border-gray-200 rounded-lg">
              {tags.length === 0 ? (
                <span className="text-sm text-gray-400">No tags added</span>
              ) : (
                tags.map(tag => (
                  <Badge key={tag} className={`${getTagColor(tag)} flex items-center gap-1`}>
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))
              )}
            </div>
          </div>

          {/* Add Custom Tag */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Add Custom Tag</label>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTag(newTag)}
                placeholder="Enter tag name"
                className="flex-1"
              />
              <Button
                onClick={() => addTag(newTag)}
                disabled={!newTag.trim()}
                size="sm"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Common Tags */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Common Tags</label>
            <div className="flex flex-wrap gap-2">
              {COMMON_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => addTag(tag)}
                  disabled={tags.includes(tag)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    tags.includes(tag)
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-purple-50 hover:border-purple-400'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            {loading ? 'Saving...' : 'Save Tags'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditPatientTagsModal;
